import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { LAYERS, portalHost } from '../lib/floating';

/**
 * A dialog that is actually on top of the page.
 *
 * ## The bug this exists to prevent, which shipped
 *
 * `position: fixed` and a big `z-index` are not enough, and the reason is
 * easy to miss: `<main>` in the shell is `relative z-10`, which makes it a
 * **stacking context**. A `z-[60]` child of `main` is z-60 *within main*, and
 * main as a whole is still z-10 — so the footer, a sibling at the same level
 * and later in the DOM, paints straight over it.
 *
 * That is exactly what happened to the terms dialog on the live system: the
 * document rendered, the footer sat on top of it, and the Accept button was
 * unreachable. Nobody could get past a gate that blocks the whole application.
 *
 * Portalling to the root escapes the trap. Every other overlay in this
 * codebase already does it — `Tooltip`, `HoverCard`, `NavFlyout`, `HelpHint`
 * and `ScrollX` — and the dialogs were the ones that did not.
 *
 * ## Why the layout is a grid rather than margins
 *
 * Header, body and footer as three rows with the body taking the slack means
 * the footer of the dialog is always visible however long the content is. The
 * first version used `max-h-[55vh]` on the body and let the whole card grow,
 * which on a short window pushed the buttons off the bottom of the screen —
 * the same class of failure as the stacking one, arrived at differently.
 */
export function Modal({
  title,
  subtitle,
  onClose,
  footer,
  wide,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Omit to make the dialog unclosable — a gate rather than a dialog. */
  onClose?: () => void;
  footer?: React.ReactNode;
  wide?: boolean;
  children: React.ReactNode;
}) {
  /*
   * Escape closes it, and the page behind does not scroll.
   *
   * A dialog you can scroll the page behind feels broken on a phone, where the
   * scroll gesture lands wherever the finger is rather than where the dialog
   * is.
   */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-navy-900/60 p-3 sm:p-4"
      style={{ zIndex: LAYERS.dialog }}
      onClick={(event) => {
        if (onClose && event.target === event.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/*
        `max-h-[calc(100dvh-1.5rem)]` and not `100vh`: on a phone browser the
        visual viewport shrinks when the address bar is showing, and `vh` does
        not know that. `dvh` does, which is the difference between a visible
        Accept button and one under the browser chrome.
      */}
      <div
        className={`grid max-h-[calc(100dvh-1.5rem)] w-full ${
          wide ? 'max-w-3xl' : 'max-w-md'
        } grid-rows-[auto_1fr_auto] overflow-hidden rounded-2xl bg-card shadow-xl`}
      >
        <div className="border-b border-border p-4 sm:p-5">
          <h2 className="font-heading text-lg">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        {/* The only part that scrolls. */}
        <div className="min-h-0 overflow-y-auto p-4 sm:p-5">{children}</div>

        {footer && <div className="border-t border-border p-4 sm:p-5">{footer}</div>}
      </div>
    </div>,
    portalHost(),
  );
}
